import React from "react";
import MuiButton from "../../../components/basicComponent/MuiButton";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RoomIcon from "@mui/icons-material/Room";
import Badge from "../../../components/basicComponent/Badge";
import { useNavigate } from "react-router-dom";

interface RoomDetailCardProps {
  room: any;
}

const RoomDetailCard: React.FC<RoomDetailCardProps> = ({ room }) => {
  const navigate = useNavigate();

  const hotelName = room?.hotelName || room?.HotelName || "Hotel Name";
  const hotelAddress =
    room?.hotelAddress || room?.HotelAddress || "Address not available";
  const rating = room?.rating ? parseInt(room.rating) : 0;
  const roomType = room?.roomType || room?.RoomType || "Signature Room";
  const checkIn = room?.CheckInDate || "N/A";
  const checkOut = room?.CheckOutDate || "N/A";
  const guestRooms = room?.Guests?.Room || room?.Rooms?.Room || [];
  const roomCount =
    room?.Guests?.Count || room?.Rooms?.Count || guestRooms.length || 1;

  const adultCount = guestRooms.reduce(
    (sum: number, r: any) => sum + (r?.NumberOfAdults || 0),
    0
  );
  const childCount = guestRooms.reduce(
    (sum: number, r: any) => sum + (r?.Child?.NumberOfChild || 0),
    0
  );

  const refundType = (room?.BookingPolicy || "").toLowerCase().includes("non")
    ? "Non-refundable"
    : "Refundable";

  // Extract amenities from Meal or MealType
  const mealString = room?.Meal || room?.MealType || "";
  const mealFeatures = mealString
    .split(",")
    .map((item: string) => item.trim())
    .filter(Boolean);

  // Calculate number of nights
  const getNightCount = (checkIn: string, checkOut: string) => {
    try {
      const inDate = new Date(checkIn);
      const outDate = new Date(checkOut);
      const diffTime = outDate.getTime() - inDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays || 1;
    } catch (e) {
      return 1;
    }
  };

  const nightCount = getNightCount(checkIn, checkOut);

  return (
    <div className="card my-3">
      <div className="row g-0">
        {/* Image Section */}
        <div className="col-md-4">
          <div className="container p-3">
            <img
              src={
                room?.HotelImage ||
                room?.ImageThumbUrl ||
                "https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&w=2074&q=80"
              }
              alt="Room"
              className="w-100 rounded-4"
              style={{ height: "334px", objectFit: "cover" }}
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="col-md-8">
          <div className="hotel-card-header border-bottom p-4">
            <div className="row">
              <div className="col-md-9">
                <h4 className="h4 fw-bold">
                  {hotelName}{" "}
                  <span className="stars" title={`${rating} Star Rating`}>
                    {"★".repeat(rating)}
                  </span>
                </h4>
                <div className="d-flex align-items-center">
                  <RoomIcon className="text-secondary" />
                  <p className="depar-info mb-0 ms-2 text-muted">
                    {hotelAddress}
                  </p>
                </div>
              </div>
              <div className="col-md-3 d-flex flex-column ">
                <p
                  className="showmore pb-1 pt-1 text-right"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => navigate("/hotellist")}
                >
                  [Change Hotel]
                  <span className="text-danger">
                    <Badge
                  borderColor={
                    refundType === "Non-refundable" ? "#d9534f" : "#2dca1c"
                  }
                  text={refundType}
                />
                  </span>
                </p>
                
              </div>
            </div>
          </div>

          {/* Stay Info */}
          <div className="card-body">
            <div className="row bg-light p-3 rounded mb-3">
              <div className="col-xl-4 col-sm-6 ">
                <h6>Check-In</h6>
                <div className="fw-bold">{checkIn}</div>
                <small className="text-muted">
                  {room?.CheckInTime || "12:00 PM"}
                </small>
              </div>
              <div className="col-xl-4 col-sm-6">
                <h6>Check-Out</h6>
                <div className="fw-bold">{checkOut}</div>
                <small className="text-muted">
                  {room?.CheckOutTime || "11:00 AM"}
                </small>
              </div>
              <div className="col-xl-4 col-sm-6">
                <h6>Guest</h6>
                <div className="fw-bold">
                  {adultCount} Adults
                  {childCount > 0 ? `, ${childCount} Children` : ""}
                </div>
                <small className="text-muted">
                  {roomCount} Room{roomCount > 1 ? "s" : ""} | {nightCount}{" "}
                  Night{nightCount > 1 ? "s" : ""}
                </small>
              </div>
            </div>

            {/* Amenities */}
            <div className="mt-2">
              <div className="d-flex justify-content-between align-items-start">
                <h3 className="h5 fw-semibold">Room Type: {roomType}</h3>
                {/* <p className="showmore pt-0">[Change Room]</p> */}
              </div>

              <div className="row row-cols-2 row-cols-md-4 g-2">
                {mealFeatures.map((item: string, idx: number) => (
                  <div key={idx} className="col">
                    <div className="d-flex align-items-center gap-2 text-success">
                      <DoneAllIcon />
                      {item}
                    </div>
                  </div>
                ))}
              </div>
              <div className="row mt-3 ">
                <h3 className="h5 fw-semibold">Cancellation Policy</h3>
                {(room?.CancellationPolicy || room?.BookingPolicy) && (
                  <div className="col">
                    <div className="d-flex align-items-center gap-2 text-secondary">
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            room?.CancellationPolicy || room?.BookingPolicy,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDetailCard;
